import { z } from 'zod';

import { listQuerySchema } from './common.js';
import { notificationChannelSchema, type NotificationChannel } from './enums.js';

/**
 * NOTIFICATIONS — in-app bildirim, kanal tercihleri, e-posta.
 *
 * Tasarım §4.2 (bildirim aksiyonu), §9 (hata bildirimi).
 *
 * ---------------------------------------------------------------------------
 * 🔴 Bildirim metni hangi dilde saklanır
 * ---------------------------------------------------------------------------
 * `Notification.title/body` düz metin kolonları. İki dilli bir üründe
 * kaydedilen metin, sonradan dil değiştiren kullanıcıya YANLIŞ DİLDE görünür
 * ve geçmiş bildirimler kalıcı olarak öyle kalır.
 *
 * Sözleşme kararı:
 *   - `type` + `data.params` SAKLANIR — asıl gerçek bunlar.
 *   - `title`/`body` kolonlarına İNGİLİZCE YEDEK metin yazılır (e-posta ve
 *     olası dışa aktarma için bir şey lazım).
 *   - Arayüz `type` + `params` ile i18n anahtarından çevirir.
 * Bu karar alınmazsa ürün fiilen tek dilli olur.
 *
 * ---------------------------------------------------------------------------
 * 🔴 SMTP kapalı — bu davranış KORUNACAK
 * ---------------------------------------------------------------------------
 * `MailService.send` bugün başarısızlıkta FIRLATMIYOR; loglayıp `false`
 * dönüyor. Bu bilinçli bir karar: davet, rapor ya da uyarı akışı posta
 * gitmedi diye çökmemeli. Bildirim satırı yine yazılır, `sentAt` boş kalır ve
 * arayüz "e-posta gönderilemedi" işareti gösterebilir.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Tip kataloğu — TEK KAYNAK
// ═══════════════════════════════════════════════════════════════════════════

export const NOTIFICATION_TYPES = [
  // otomasyon
  'automation.approval_pending',
  'automation.action_applied',
  'automation.action_failed',
  'automation.run_skipped',
  /**
   * Faz 3 — "kural sana haber vermek istedi".
   *
   * `notify` / `notify_external` aksiyonunun ÜRETTİĞİ bildirim. Faz 3'e kadar
   * `automation.action_applied` tipini EZİYORDU: iki farklı olay tek tipe
   * yazılıyordu ve sonuçları somuttu —
   *
   *   • Kullanıcı "otomasyon bir değişiklik uyguladı" bildirimlerini kapatınca
   *     "kural seni uyardı" bildirimleri de susuyordu. İkisi aynı şey değil:
   *     biri yapılmış bir işin kaydı, diğeri kullanıcının kurduğu uyarının ta
   *     KENDİSİ. Uyarıyı kapatmak için kuralı silmek gerekirdi.
   *   • `/admin/notifications` sayacı iki olayı topluyordu.
   *   • Metin `titleKey`/`bodyKey` ile eziliyordu; ezme unutulsa
   *     "Automation applied a change" yazacaktı — hiçbir şey uygulanmadığı hâlde.
   */
  'automation.rule_notified',
  // bağlantı
  'connection.disconnected',
  'connection.degraded',
  'connection.recovered',
  'sync.failed',
  // kreatif
  'creative.ready',
  'creative.failed',
  'creative.refunded',
  /**
   * Faz 3 — "stüdyoda seni bekleyen bir iyileştirme görevi var".
   *
   * `creative_improve` aksiyonunun açtığı DRAFT görev. Bu da
   * `automation.action_applied`ı eziyordu ve orada bir de BOZUK GÖVDE vardı:
   * o tipin metni `{ruleName}` / `{targetName}` bekliyor, çağıran ise
   * `params: { creativeType }` gönderiyordu. Yer tutucular boş doldurulup
   * ekranda `" updated."` gibi bir cümle çıkıyordu.
   *
   * Kendi tipi olunca metin de kendi parametreleriyle yazılabiliyor.
   *
   * 🔴 `creative.ready` DEĞİL: o, üretimi BİTMİŞ bir kreatifi anlatıyor ve
   * kullanıcı ona tıklayınca hazır görseli bekliyor. Burada henüz hiçbir şey
   * üretilmedi — kredi bile harcanmadı, görev stüdyoda taslak olarak duruyor.
   */
  'creative.task_opened',
  /**
   * Şema borç turu — "bir kreatif senin onayını bekliyor".
   *
   * 🔴 `automation.approval_pending` YENİDEN KULLANILMADI. O tipin metni
   * *"«{ruleName}» kuralı {targetName} üzerinde bir değişiklik önerdi"*
   * diyor; kreatif onayında `ruleName` gizli sistem kuralının adı
   * (`__creative__`) olurdu ve kullanıcı kurmadığı bir kuralın adını
   * e-postada görürdü. `creative.task_opened`ın Faz 3'te düzelttiği hatanın
   * birebir aynısı — tipi ezmek, metni bozar.
   */
  'creative.approval_pending',
  /// Kreatif onaylandı ya da reddedildi — ÜRETİCİYE gider (`audience: actor`).
  /// Onaycıya değil: kararı zaten o verdi.
  'creative.approval_decided',
  /**
   * ÜRÜN GÖRSELİ ANALİZİ BİTTİ (21 Ağu 2026).
   *
   * 🔴 `creative.ready` YENİDEN KULLANILMADI ve gerekçe `creative.task_opened`
   * ile birebir aynı: o tip ÜRETİLMİŞ bir kreatifi anlatıyor, kullanıcı ona
   * tıklayınca hazır bir görsel/video bekliyor. Burada üretilmiş bir şey yok —
   * kullanıcının stüdyoya dönüp üç konseptten birini SEÇMESİ gerekiyor ve
   * asıl üretim ondan sonra başlıyor. Tipi ezmek, metni bozar.
   *
   * Bildirimin var olma sebebi kredi: analiz worker'da koşuyor ve kullanıcı
   * sekmeyi kapatsa da sürüyor. Bittiğini söyleyen bir şey olmasa, ödenmiş 2
   * kredinin karşılığı kullanıcı stüdyoya kendiliğinden dönene kadar
   * beklerdi.
   */
  'creative.vision_ready',
  /// Analiz terminal hatayla kapandı ve KREDİ İADE EDİLDİ. Ayrı tip: "hazır"
  /// bildiriminin gövdesi konsept sayısını yazıyor, burada konsept yok.
  'creative.vision_failed',
  // faturalama
  'billing.credit_low',
  'billing.credit_exhausted',
  'billing.limit_warning',
  'billing.grace_started',
  'billing.grace_ended',
  'billing.payment_failed',
  /// Deneme bitişine az kaldı. Ayrı tip: `limit_warning` HARCAMA sınırını
  /// anlatıyor, bu ise SÜRENİN dolduğunu — ikisi aynı anda da olabilir.
  'billing.trial_ending',
  /// Deneme bitti, abonelik EXPIRED. Kullanıcı plan seçene kadar otomasyon,
  /// kreatif üretimi ve ajanın yazma araçları duruyor.
  'billing.trial_ended',
  // ekip
  'team.invitation_sent',
  'team.member_joined',
  'team.role_changed',
  // rapor
  'report.ready',
  'report.scheduled_sent',
  // veri
  'data.export_ready',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const NOTIFICATION_GROUPS = [
  'automation',
  'connection',
  'creative',
  'billing',
  'team',
  'report',
  'data',
] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export interface NotificationTypeDefinition {
  type: NotificationType;
  group: NotificationGroup;
  /// Varsayılan kanallar. Kullanıcı tercihi bunu ezebilir — `critical` hariç.
  defaultChannels: NotificationChannel[];
  /**
   * BD6 — kritik bildirimler KAPATILAMAZ.
   * Kopuk hesap, ödeme hatası, kredi bitti, otomasyon hatası: kullanıcı
   * bunları görmezse ürün sessizce çalışmayı bırakır ve o bunu haftalar sonra
   * fark eder.
   */
  critical: boolean;
  /// Kime gider.
  audience: 'owners_admins' | 'all_members' | 'actor' | 'specific_user';
}

export const NOTIFICATION_CATALOG: Record<NotificationType, NotificationTypeDefinition> = {
  'automation.approval_pending': { type: 'automation.approval_pending', group: 'automation', defaultChannels: ['IN_APP', 'EMAIL'], critical: false, audience: 'owners_admins' },
  'automation.action_applied': { type: 'automation.action_applied', group: 'automation', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'automation.action_failed': { type: 'automation.action_failed', group: 'automation', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'automation.run_skipped': { type: 'automation.run_skipped', group: 'automation', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  // Varsayılan kanal `notify` aksiyonunun kendi `channels` alanıyla EZİLİYOR
  // (kural yazarı hangi kanalı istediğini zaten söylüyor). Buradaki değer
  // yalnız o alan verilmediğinde geçerli. `critical: false` — kullanıcının
  // kurduğu bir uyarıyı yine kullanıcı kapatabilmeli.
  'automation.rule_notified': { type: 'automation.rule_notified', group: 'automation', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'connection.disconnected': { type: 'connection.disconnected', group: 'connection', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'connection.degraded': { type: 'connection.degraded', group: 'connection', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'connection.recovered': { type: 'connection.recovered', group: 'connection', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'sync.failed': { type: 'sync.failed', group: 'connection', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'creative.ready': { type: 'creative.ready', group: 'creative', defaultChannels: ['IN_APP'], critical: false, audience: 'actor' },
  'creative.failed': { type: 'creative.failed', group: 'creative', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'actor' },
  'creative.refunded': { type: 'creative.refunded', group: 'creative', defaultChannels: ['IN_APP'], critical: true, audience: 'actor' },
  // `audience: 'all_members'` — görevi bir KURAL açtı, bir kişi değil.
  // `actor` deseydik hedef, kuralı aylar önce kuran kişi olurdu; stüdyoya
  // bugün bakan başkasıysa görev sessizce beklerdi.
  'creative.task_opened': { type: 'creative.task_opened', group: 'creative', defaultChannels: ['IN_APP'], critical: false, audience: 'all_members' },
  // `owners_admins` — kararı verecek olanlar. `automation.approval_pending`
  // ile aynı hedef kitle ve aynı gerekçe: onay bir yönetim işi.
  'creative.approval_pending': { type: 'creative.approval_pending', group: 'creative', defaultChannels: ['IN_APP', 'EMAIL'], critical: false, audience: 'owners_admins' },
  // `actor` — kreatifi onaya gönderen kişi. Kararı ondan başkası beklemiyor.
  'creative.approval_decided': { type: 'creative.approval_decided', group: 'creative', defaultChannels: ['IN_APP'], critical: false, audience: 'actor' },
  // `actor` — analizi başlatan (ve 2 kredisini veren) kişi. Ekibin geri
  // kalanı o fotoğrafı hiç görmedi.
  //
  // ⚠️ Kanal yalnız `IN_APP` ve bu bilinçli: satırı yazan yer worker
  // (`vision-notify.ts`) ve orası `NotificationService`e ulaşamıyor, yani
  // e-posta gönderemiyor. Katalogda `EMAIL` yazsaydı tercih ekranı
  // kullanıcıya hiç gelmeyecek bir e-postayı açık/kapalı gösterirdi.
  'creative.vision_ready': { type: 'creative.vision_ready', group: 'creative', defaultChannels: ['IN_APP'], critical: false, audience: 'actor' },
  // `critical: false` — kredi zaten OTOMATİK iade ediliyor, kullanıcının
  // yapması gereken bir şey yok. Kapatılabilir olması doğru.
  'creative.vision_failed': { type: 'creative.vision_failed', group: 'creative', defaultChannels: ['IN_APP'], critical: false, audience: 'actor' },
  'billing.credit_low': { type: 'billing.credit_low', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: false, audience: 'owners_admins' },
  'billing.credit_exhausted': { type: 'billing.credit_exhausted', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'billing.limit_warning': { type: 'billing.limit_warning', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'billing.grace_started': { type: 'billing.grace_started', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'billing.grace_ended': { type: 'billing.grace_ended', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'billing.payment_failed': { type: 'billing.payment_failed', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  // Süre uyarısı kapatılabilir (`critical: false`): kullanıcı denemenin
  // bittiğini zaten panelde görüyor ve bu bir HATIRLATMA. Bitişin kendisi
  // kapatılamaz — o an ürünün bir kısmı gerçekten duruyor.
  'billing.trial_ending': { type: 'billing.trial_ending', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: false, audience: 'owners_admins' },
  'billing.trial_ended': { type: 'billing.trial_ended', group: 'billing', defaultChannels: ['IN_APP', 'EMAIL'], critical: true, audience: 'owners_admins' },
  'team.invitation_sent': { type: 'team.invitation_sent', group: 'team', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'team.member_joined': { type: 'team.member_joined', group: 'team', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'team.role_changed': { type: 'team.role_changed', group: 'team', defaultChannels: ['IN_APP'], critical: false, audience: 'specific_user' },
  'report.ready': { type: 'report.ready', group: 'report', defaultChannels: ['IN_APP'], critical: false, audience: 'actor' },
  'report.scheduled_sent': { type: 'report.scheduled_sent', group: 'report', defaultChannels: ['IN_APP'], critical: false, audience: 'owners_admins' },
  'data.export_ready': { type: 'data.export_ready', group: 'data', defaultChannels: ['IN_APP', 'EMAIL'], critical: false, audience: 'actor' },
};

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NotificationService imzası — DİĞER ALANLARIN SÖZLEŞMESİ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `NotificationService.notify(input: NotifyInput): Promise<void>`
 *
 * Bildirim üreten alanlar (automation, integrations, billing, creative,
 * workspaces) bu tek metodu çağırır. Kimse `prisma.notification.create()`
 * yazmaz — yazarsa kanal tercihi, dedupe ve kritik-bildirim kuralı atlanır.
 *
 * `Notification.userId` nullable olduğu için workspace geneli bildirim
 * şemada zaten mümkün; `target.type = 'workspace'` bunu kullanır.
 */
export interface NotifyInput {
  workspaceId: string;
  target:
    | { type: 'user'; userId: string }
    | { type: 'role'; roles: ('OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER')[] }
    | { type: 'workspace' };
  type: NotificationType;
  /// i18n anahtarları. Katalogdan türetilebilir ama açıkça verilmesi
  /// mümkün — özel metinli `notify` aksiyonu bunu kullanıyor.
  titleKey?: string;
  bodyKey?: string;
  /// Çeviriye giden değişkenler ("CPC $2.40", "Meta Ads Hesabı").
  params?: Record<string, string | number>;
  /// BD4 — kaynağa sıçrama. HAM URL SAKLANMAZ; rota değişince eski
  /// bildirimler kırılır. Arayüz rotayı `entity` + `id`'den üretir.
  data?: {
    entity: 'campaign' | 'workflow' | 'workflow_action' | 'creative' | 'vision_analysis' | 'connection' | 'report' | 'invoice' | 'member';
    id: string;
    [key: string]: unknown;
  };
  /// Katalog varsayılanını ezer. Kritik tiplerde EMAIL kanalı çıkarılamaz.
  channels?: NotificationChannel[];
  /**
   * Gürültü kontrolü. Aynı anahtar TTL süresince ikinci kez bildirim üretmez.
   * Olmadan bildirim listesi kullanılamaz hâle gelir: senkron 15 dakikada bir
   * deniyor ve her denemede "bağlantı koptu" yazsaydı gün sonunda 96 satır.
   */
  dedupeKey?: string;
  dedupeTtlSeconds?: number;
}

/// Önerilen dedupe pencereleri.
export const DEDUPE_TTL = {
  connectionDisconnected: 15 * 60,
  syncFailed: 60 * 60,
  automationActionFailed: 60 * 60,
  /// Dönem başına tek uyarı.
  creditLow: 30 * 24 * 60 * 60,
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// Uçlar
// ═══════════════════════════════════════════════════════════════════════════

export interface NotificationRow {
  id: string;
  type: string;
  group: NotificationGroup | null;
  /// İngilizce yedek metin (yukarıdaki çok dillilik kararına bak).
  title: string;
  body: string | null;
  /// Arayüzün çeviri için kullanacağı asıl veri.
  params: Record<string, string | number> | null;
  data: Record<string, unknown> | null;
  channel: NotificationChannel;
  readAt: string | null;
  sentAt: string | null;
  critical: boolean;
  createdAt: string;
}

export const notificationListQuerySchema = listQuerySchema.extend({
  unreadOnly: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  group: z.enum(NOTIFICATION_GROUPS).optional(),
  type: z.string().max(60).optional(),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

/// KB6 — kabuktaki zil her sayfada çağırıyor; ayrı ve hafif uç.
export interface UnreadCountResponse {
  unread: number;
  /// Gruba göre kırılım — zil menüsü sekmelerini besliyor.
  byGroup: Record<NotificationGroup, number>;
  /// Okunmamış KRİTİK var mı — zil kırmızı yanar.
  hasCritical: boolean;
}

export const markReadSchema = z.object({
  /// Boş bırakılırsa (ya da `all: true`) hepsi okundu işaretlenir.
  ids: z.array(z.uuid()).max(200).optional(),
  all: z.boolean().default(false),
  group: z.enum(NOTIFICATION_GROUPS).optional(),
});
export type MarkReadInput = z.infer<typeof markReadSchema>;

/**
 * BD5 — kanal tercihleri.
 *
 * 🔴 ŞEMADA SAKLAMA YERİ YOK. `Notification` var, tercih tablosu yok ve şema
 * donmuş. Bu turdaki çözüm: Redis'te `notifprefs:<workspaceId>:<userId>`
 * hash'i. Kalıcılık ZAYIF (Redis kalıcılığı yapılandırmaya bağlı) ve bu
 * bilinçli bir ödünç — tercihini kaybeden kullanıcı varsayılana döner,
 * veri kaybetmez.
 *
 * Kalıcı çözüm bir tablo ister; öneri `semaDegisikligi`'nde.
 */
export interface NotificationPreference {
  type: NotificationType;
  group: NotificationGroup;
  email: boolean;
  inApp: boolean;
  /// Kritik tipler `locked: true` döner ve PUT'ta YOK SAYILIR.
  locked: boolean;
}

export const preferencesUpdateSchema = z.object({
  preferences: z
    .array(
      z.object({
        type: z.enum(NOTIFICATION_TYPES),
        email: z.boolean(),
        inApp: z.boolean(),
      }),
    )
    .max(NOTIFICATION_TYPES.length),
});
export type PreferencesUpdateInput = z.infer<typeof preferencesUpdateSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Admin — SALT OKUNUR envanter
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `/admin/notifications` bu turda yalnız GÖRÜNTÜLER: hangi bildirim tipleri
 * var, hangileri kritik, hangi e-posta şablonları tanımlı, SMTP açık mı.
 *
 * "Herkese duyuru gönder" düğmesi YAZILMAZ: test edilmemiş bir toplu gönderim
 * yüzeyi, yanlış basılınca geri alınamaz. POSTA şablonu DÜZENLEME de yok —
 * şablonlar `@mentorivo/shared/mail`de kod sabiti ve düzenlemeyi saklayacak bir
 * tablo yok; düzenleme ekranı yalan söyleyen bir düğme olurdu.
 *
 * ⚠️ Faz 3'te OTOMASYON şablonları (`/admin/templates`) düzenlenebilir oldu;
 * bu ekran değil. Otomasyon şablonu VERİ, posta şablonu satır içi stilli HTML —
 * ikincisini metin kutusundan düzenletmek, bozuk bir gövdenin doğrudan gelen
 * kutusuna gitmesi demekti.
 */
export interface AdminNotificationTypeRow extends NotificationTypeDefinition {
  /// Bu tipten kaç bildirim üretilmiş (tüm kiracılar).
  totalCount: number;
  lastSentAt: string | null;
}

export interface AdminMailTemplateRow {
  key: string;
  subjectPreview: string;
  /// Hangi dillerde tanımlı.
  locales: string[];
}

export interface AdminNotificationsOverview {
  types: AdminNotificationTypeRow[];
  templates: AdminMailTemplateRow[];
  smtp: {
    /// SMTP_HOST dolu mu. DEĞER DÖNMEZ.
    configured: boolean;
    /// Son gönderim denemesi başarılı mıydı.
    lastSendOk: boolean | null;
    lastAttemptAt: string | null;
  };
}

export const NotificationErrorCode = {
  NOTIFICATION_NOT_FOUND: 'NOTIFICATION_NOT_FOUND',
  /// Kritik tipin kanalı kapatılamaz — istek kısmen uygulanır ve bu kod
  /// `details.ignoredTypes` ile döner.
  CRITICAL_TYPE_LOCKED: 'CRITICAL_TYPE_LOCKED',
  UNKNOWN_TYPE: 'UNKNOWN_TYPE',
} as const;
export type NotificationErrorCode =
  (typeof NotificationErrorCode)[keyof typeof NotificationErrorCode];
